import React from 'react';

import getDataGridStyles from '@dashboard/components/data-grids/helpers/getDataGridStyles';
import { Box, CircularProgress } from '@mui/material';
import { DataGrid, GridColDef, GridValidRowModel } from '@mui/x-data-grid';

type THocColumn<TNode extends GridValidRowModel> = {
  headerAlign?: GridColDef<TNode>['headerAlign'];
  align?: GridColDef<TNode>['align'];
  minWidth?: GridColDef<TNode>['minWidth'];
  maxWidth?: GridColDef<TNode>['maxWidth'];
  headerName?: GridColDef<TNode>['headerName'];
  renderCell: NonNullable<GridColDef<TNode>['renderCell']>;
};

type THocProps<TNode extends GridValidRowModel> = {
  onRowClick?: (model: TNode) => void;
  nodes?: TNode[];
};

type TCreateTableProps<TNode extends GridValidRowModel> = {
  columns: THocColumn<TNode>[];
  emptyResults: JsxNode;
};
export function createTableComponent<TNode extends GridValidRowModel>({
  emptyResults,
  columns,
}: TCreateTableProps<TNode>) {
  const memoizedColumns: GridColDef<TNode>[] = columns.map(({ minWidth, ...column }, index) => ({
    ...column,
    minWidth: minWidth ?? 160,
    flex: 1,
    type: 'string',
    field: `field-${index}`,
    sortable: false,
  }));

  return ({ nodes, onRowClick }: THocProps<TNode>) => {
    if (!nodes) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 96 }}>
          <CircularProgress size={32} />
        </Box>
      );
    }

    if (nodes.length === 0) {
      return emptyResults;
    }

    return (
      <WpDataGrid<TNode>
        onRowClick={(row) => {
          if (onRowClick) {
            onRowClick(row);
          }
        }}
        columns={memoizedColumns}
        nodes={nodes}
      />
    );
  };
}

type WpDataGrid<TNode extends GridValidRowModel> = {
  onRowClick?: (model: TNode) => void;
  nodes: TNode[];
  columns: GridColDef<TNode>[];
};
export function WpDataGrid<TNode extends GridValidRowModel>({ onRowClick, nodes, columns }: WpDataGrid<TNode>) {
  return (
    <DataGrid
      disableColumnFilter
      disableSelectionOnClick
      disableColumnMenu
      disableColumnSelector
      disableDensitySelector
      disableExtendRowFullWidth
      showColumnRightBorder={false}
      autoHeight
      headerHeight={40}
      onRowClick={({ row }) => {
        if (onRowClick) {
          onRowClick(row);
        }
      }}
      sx={getDataGridStyles({ isClickableRow: true })}
      columns={columns}
      rows={nodes}
      filterMode="server"
      sortingMode="server"
      hideFooter
    />
  );
}
